import type { AxiosResponse } from 'axios';

import api from '../axios';
import type {
  propsApiContract,
  propsApiContractListResponse,
  propsApiExtractionResponse,
  propsApiInstitutionLite,
  propsRequestCreateContractBody,
  propsRequestListContractsQuery,
} from '../interfaces';

export function listInstitutionsRequest(): Promise<
  AxiosResponse<{ items: propsApiInstitutionLite[] }>
> {
  return api.get('/v1/institutions');
}

function toQueryString(q: propsRequestListContractsQuery): string {
  const params: string[] = [];
  if (q.cursor) params.push(`cursor=${encodeURIComponent(q.cursor)}`);
  if (q.limit !== undefined) params.push(`limit=${q.limit}`);
  if (q.fields) params.push(`fields=${q.fields}`);
  if (q.status?.length) params.push(`status=${q.status.join(',')}`);
  if (q.modality?.length) params.push(`modality=${q.modality.join(',')}`);
  if (q.riskLevel?.length) params.push(`riskLevel=${q.riskLevel.join(',')}`);
  if (q.institutionId) params.push(`institutionId=${q.institutionId}`);
  if (q.search) params.push(`search=${encodeURIComponent(q.search)}`);
  return params.length ? `?${params.join('&')}` : '';
}

export function listContractsRequest(
  query: propsRequestListContractsQuery = {},
): Promise<AxiosResponse<propsApiContractListResponse>> {
  return api.get(`/v1/contracts${toQueryString(query)}`);
}

export function getContractRequest(
  id: string,
): Promise<AxiosResponse<propsApiContract>> {
  return api.get(`/v1/contracts/${id}`);
}

export function createContractRequest(
  body: propsRequestCreateContractBody,
  idempotencyKey?: string,
): Promise<AxiosResponse<propsApiContract>> {
  return api.post('/v1/contracts', body, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

export function updateContractRequest(
  id: string,
  body: {
    contractNumber?: string;
    guarantee?: string | null;
    cropDescription?: string | null;
  },
): Promise<AxiosResponse<propsApiContract>> {
  return api.patch(`/v1/contracts/${id}`, body);
}

export function deleteContractRequest(id: string) {
  return api.delete(`/v1/contracts/${id}`);
}

export function recomputeContractRequest(
  id: string,
): Promise<AxiosResponse<propsApiContract>> {
  return api.post(`/v1/contracts/${id}/recompute`);
}

export function extractContractRequest(
  file: File,
): Promise<AxiosResponse<propsApiExtractionResponse>> {
  const form = new FormData();
  form.append('file', file);
  return api.post('/v1/contracts/extract', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export function getExtractionRequest(
  id: string,
): Promise<AxiosResponse<propsApiExtractionResponse>> {
  return api.get(`/v1/contracts/extract/${id}`);
}
